/**
 * Fechas de una licencia. Modulo PLANO, sin React, y eso es parte del diseno:
 * las pruebas del servidor lo importan, y alli no existe `react` instalado.
 *
 * Vivia dentro de LicenseForm.jsx y la prueba lo importaba de ahi. En local
 * pasaba —el repo tiene las dependencias del CRM instaladas— y en el servidor
 * de construccion tumbo el despliegue entero: "Cannot find package 'react'"
 * (2026-08-20). El resto de pruebas que miran codigo del CRM importan .js
 * planos; esta era la unica que importaba un .jsx.
 */

/**
 * La fecha de emisión más dos años.
 *
 * Wendy Cercio, 2026-08-20: "como el documento no tiene fecha de vencimiento le
 * puedes colocar + 2 años de la fecha de vigencia". Es la renovación habitual
 * de una licencia de productor en casi todos los estados.
 *
 * Va detrás de un BOTÓN, nunca automático, y esa es la decisión importante: la
 * fecha de expiración manda las alertas de "licencia por vencer". Una que
 * Deskte hubiera inventado sola se leería igual que una copiada del documento
 * —sin forma de distinguirlas— y un agente podría quedarse tranquilo con una
 * licencia caducada, o renovar meses antes de tiempo. Puesta con un clic, la
 * fecha es de quien la puso, que es lo que Wendy pedía.
 *
 * Devuelve "" si la fecha de origen no sirve, para que el botón no pueda
 * ensuciar el campo.
 */
export function masDosAnos(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return "";
  const [, y, mes, dia] = m;
  const d = new Date(Number(y) + 2, Number(mes) - 1, Number(dia));
  if (isNaN(d.getTime())) return "";
  // 29 de febrero: dos años después ese día no existe y el navegador lo pasa
  // al 1 de marzo — o sea, un vencimiento un día MÁS TARDE del que toca.
  // setDate(0) lo devuelve al último día de febrero, que es lo que hace
  // cualquier renovación de verdad.
  if (d.getMonth() !== Number(mes) - 1) d.setDate(0);
  const p = (n) => String(n).padStart(2, "0");
  // Partes locales, nunca toISOString: en UTC la fecha se va un día atrás para
  // media America.
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── Fechas de un CERTIFICADO ────────────────────────────────────────────────
//
// Lissette, 2026-09-03: *"cuando se sube un certificado toma fecha de
// expiración como la fecha de completación del certificado; es muy diferente a
// licencia, ojo con eso"*.
//
// POR QUE PASABA. Un PDF de licencia estatal trae el texto desordenado y en
// tablas, asi que buscar la fecha por la etiqueta de al lado agarra la
// equivocada. La estrategia que funciona ahi es otra: de todas las fechas
// creibles del documento, la MAS TEMPRANA es la emision y la MAS TARDIA el
// vencimiento. Verificado contra PDFs reales de AZ, MD, TN y KS.
//
// Un CERTIFICADO no tiene esa forma. No se emite y vence: se COMPLETA, y la
// fecha que trae es la de completacion. Aplicarle la regla de la licencia
// significa que la fecha de completacion acaba en la casilla de vencimiento
// (si es la ultima del documento) o que se inventa un vencimiento a partir de
// la fecha de impresion. Las dos cosas ensucian las alertas de "por vencer".
//
// La regla aqui, entonces:
//   1. La fecha de COMPLETACION es la que lo dice con su etiqueta.
//   2. Si ninguna lo dice, es la mas TEMPRANA de las que no sean el
//      vencimiento — una fecha de impresion siempre es posterior.
//   3. El VENCIMIENTO solo se pone si el documento lo dice con todas las
//      letras. NO se deduce: un vencimiento inventado se lee igual que uno
//      copiado del papel, y con eso se manda una alerta que no toca (o se
//      calla la que si).

/** Fecha suelta -> YYYY-MM-DD, o "" si no se reconoce. */
export function fechaIso(s) {
  if (!s) return '';
  s = String(s).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return s;
  const mdy = s.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (mdy) {
    let y = mdy[3];
    if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
    return `${y}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  }
  const mos = {
    january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
    july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
    jan: '01', feb: '02', mar: '03', apr: '04', jun: '06', jul: '07', aug: '08',
    sep: '09', oct: '10', nov: '11', dec: '12',
  };
  const mdy2 = s.match(/([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s*(\d{4})/);
  if (mdy2 && mos[mdy2[1].toLowerCase()]) return `${mdy2[3]}-${mos[mdy2[1].toLowerCase()]}-${mdy2[2].padStart(2, '0')}`;
  const dmy = s.match(/(\d{1,2})\s+([A-Za-z]{3,9})\.?\s*(\d{4})/);
  if (dmy && mos[dmy[2].toLowerCase()]) return `${dmy[3]}-${mos[dmy[2].toLowerCase()]}-${dmy[1].padStart(2, '0')}`;
  return '';
}

const D_SUELTA = '(\\d{1,2}[/-]\\d{1,2}[/-]\\d{2,4}|\\d{4}-\\d{2}-\\d{2}|[A-Za-z]{3,9}\\.?\\s+\\d{1,2},?\\s*\\d{4}|\\d{1,2}\\s+[A-Za-z]{3,9}\\.?\\s*\\d{4})';
const HUECO = '\\s*[:#\\-]?\\s*(?:[^\\d\\n]{0,40}?)';
const ETIQUETA_COMPLETADO = '(?:Date\\s*Completed|Completion\\s*Date|Date\\s*of\\s*Completion|Completed(?:\\s*On)?|Fecha\\s*(?:de\\s*)?(?:Completaci[oó]n|Finalizaci[oó]n|Terminaci[oó]n)|Completado(?:\\s*el)?|Finalizado(?:\\s*el)?)';
const ETIQUETA_VENCE = '(?:Expir(?:ation|y|es|e)?(?:\\s*(?:On|Date))?|Exp\\.?\\s*(?:Date)?|Valid\\s*(?:Through|Thru|Until|To)|Good\\s*(?:Through|Until)|Fecha\\s*(?:de\\s*)?(?:Expiraci[oó]n|Vencimiento|Caducidad))';

/**
 * @param {string} textoPlano  El texto del PDF ya aplanado.
 * @param {string[]} fechasOrdenadas  Las fechas creibles del documento (ISO, de
 *   menor a mayor) que ya calculo quien llama.
 * @returns {{issuedDate: string, expiryDate: string}}
 */
export function fechasDeCertificado(textoPlano, fechasOrdenadas = []) {
  const t = String(textoPlano || '');
  const buscar = (etiqueta) => {
    const m = t.match(new RegExp(etiqueta + HUECO + D_SUELTA, 'i'));
    return m ? fechaIso(m[1]) : '';
  };
  const expiryDate = buscar(ETIQUETA_VENCE);
  let issuedDate = buscar(ETIQUETA_COMPLETADO);
  if (!issuedDate) {
    issuedDate = (fechasOrdenadas || []).find((f) => f && f !== expiryDate) || '';
  }
  return { issuedDate, expiryDate };
}
